import { useState } from "react";

interface VenueGalleryProps {
  coverImage: string;
  gallery: string[];
  venueName: string;
}

export default function VenueGallery({ coverImage, gallery, venueName }: VenueGalleryProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const allImages = [coverImage, ...gallery];

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImage = () => setLightboxIdx((prev) => (prev !== null ? (prev - 1 + allImages.length) % allImages.length : 0));
  const nextImage = () => setLightboxIdx((prev) => (prev !== null ? (prev + 1) % allImages.length : 0));

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") prevImage();
    if (e.key === "ArrowRight") nextImage();
  };

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[480px] md:h-[520px] overflow-hidden rounded-2xl">
        {/* Main large image */}
        <div
          className="col-span-4 md:col-span-2 row-span-2 relative cursor-pointer group overflow-hidden"
          onClick={() => openLightbox(0)}
        >
          <img
            src={coverImage}
            alt={venueName}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
        </div>

        {/* Small gallery images */}
        {gallery.slice(0, 4).map((img, i) => (
          <div
            key={i}
            className={`relative cursor-pointer group overflow-hidden ${i >= 2 ? "hidden md:block" : ""}`}
            onClick={() => openLightbox(i + 1)}
          >
            <img
              src={img}
              alt={`${venueName} ${i + 2}`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            {/* Show "more" on last visible */}
            {i === 1 && gallery.length > 2 && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center md:hidden">
                <span className="text-white font-semibold text-lg">+{gallery.length - 2} Fotoğraf</span>
              </div>
            )}
            {i === 3 && gallery.length > 4 && (
              <div className="absolute inset-0 bg-black/40 hidden md:flex items-center justify-center">
                <span className="text-white font-semibold text-lg">+{gallery.length - 4} Fotoğraf</span>
              </div>
            )}
          </div>
        ))}

        {/* View all button */}
        <button
          onClick={() => openLightbox(0)}
          className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm text-stone-800 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-white transition-colors whitespace-nowrap cursor-pointer hidden md:flex"
          style={{ position: "absolute", bottom: "1rem", right: "1rem" }}
        >
          <i className="ri-image-line text-base" />
          Tüm Fotoğraflar ({allImages.length})
        </button>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <div className="relative w-full max-w-5xl px-4" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>

            {/* Prev */}
            <button
              onClick={prevImage}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>

            {/* Image */}
            <img
              src={allImages[lightboxIdx]}
              alt={`${venueName} ${lightboxIdx + 1}`}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />

            {/* Counter */}
            <div className="text-center text-white/60 text-sm mt-4">
              {lightboxIdx + 1} / {allImages.length}
            </div>

            {/* Next */}
            <button
              onClick={nextImage}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>

            {/* Thumbnails */}
            <div className="flex gap-2 justify-center mt-4 overflow-x-auto pb-2">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setLightboxIdx(idx)}
                  className={`flex-shrink-0 w-14 h-10 rounded-md overflow-hidden cursor-pointer transition-all ${idx === lightboxIdx ? "ring-2 ring-white opacity-100" : "opacity-50 hover:opacity-80"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
